import React, { Dispatch, MutableRefObject, SetStateAction, useLayoutEffect, useRef } from "react";
import { APP_CONTAINER_NODE_CLASSNAME } from "shared/constants/common.constants";
import { AppDOM } from "shared/utils/app.utils";
import styles from "./InputAddTag.module.scss";

import { calcWidthInput, trimSpaceAndComma } from "../EditTags.utils";

const testRegExp = /^[a-zA-Z0-9-]{3,33}$/;
const testAddRegExp = /^[a-zA-Z0-9-]{3,33}[\s,]$/;

type InputAddTagProps = {
  addTag: (tag: string) => void;
  checkRepeatTag: (tag: string) => boolean;
  setError: Dispatch<SetStateAction<boolean>>;
  setRepeat: Dispatch<SetStateAction<boolean>>;
  setSearchTag: Dispatch<SetStateAction<string>>;
};

export const InputAddTag = React.forwardRef<
  HTMLInputElement,
  InputAddTagProps & React.InputHTMLAttributes<HTMLInputElement>
>(({ addTag, checkRepeatTag, setError, setRepeat, setSearchTag, ...props }, ref) => {
  const appContainer = AppDOM.querySelector<HTMLDivElement>(`.${APP_CONTAINER_NODE_CLASSNAME}`);
  const elemCalcWidth = useRef<HTMLPreElement>(document.createElement("pre"));
  elemCalcWidth.current.classList.add(styles.calcWidth);

  const onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    props.onFocus && props.onFocus(event);
    appContainer?.appendChild(elemCalcWidth.current);
  };

  const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    props.onBlur && props.onBlur(event);
    appContainer?.removeChild(elemCalcWidth.current);
    const valueInput = event.currentTarget.value;
    if (!checkRepeatTag(trimSpaceAndComma(valueInput)) && testRegExp.test(valueInput)) {
      addTag(trimSpaceAndComma(valueInput));
      event.currentTarget.value = "";
    }
    if (!event.currentTarget.value) {
      setSearchTag("");
      setError(false);
    }
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    props.onChange && props.onChange(event);
    calcWidthInput(event.currentTarget, elemCalcWidth.current);

    const valueInput = event.currentTarget.value;
    event.currentTarget.value = trimSpaceAndComma(valueInput);
    setSearchTag(trimSpaceAndComma(valueInput));

    if (!testRegExp.test(trimSpaceAndComma(valueInput))) {
      setError(true);
      setRepeat(false);
      return;
    }

    if (checkRepeatTag(trimSpaceAndComma(valueInput))) {
      setRepeat(true);
      setError(false);
      return;
    }
    setRepeat(false);
    setError(false);

    if (testAddRegExp.test(valueInput)) {
      addTag(valueInput.substring(0, valueInput.length - 1));
      event.currentTarget.value = "";
      setSearchTag("");
    }
  };

  useLayoutEffect(() => {
    calcWidthInput((ref as MutableRefObject<HTMLInputElement>).current, elemCalcWidth.current);
  });

  return (
    <input
      {...props}
      onFocus={onFocus}
      onBlur={onBlur}
      onChange={onChange}
      ref={ref}
      defaultValue=""
      type="text"
      className={styles.input}
    />
  );
});

InputAddTag.displayName = "InputAddTag";
